import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { FieldTypeEnum } from '@isophi/mng-api';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

/**
 * UC4 — edit the value of a historical (non-current) record.
 * Renders a dynamic input based on the field type. An empty value is allowed.
 */
@Component({
  selector: 'app-history-value-edit-modal',
  templateUrl: './history-value-edit-modal.component.html',
  styleUrls: ['./history-value-edit-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class HistoryValueEditModalComponent implements OnInit {
  @Input() fieldLabel: string;
  @Input() fieldType: FieldTypeEnum = FieldTypeEnum.Text;
  @Input() fieldOptions: Array<{ value: unknown; label: string }> = [];
  @Input() currentValue: unknown;
  @Input() validFrom: string;

  readonly fieldTypes = FieldTypeEnum;

  /** True when the field has a fixed set of selectable options (choice / country / relation). */
  get hasOptions(): boolean {
    return Array.isArray(this.fieldOptions) && this.fieldOptions.length > 0;
  }

  get validFromLabel(): string {
    if (!this.validFrom) {
      return '';
    }
    const d = new Date(this.validFrom);
    if (Number.isNaN(d.getTime())) {
      return this.validFrom;
    }
    return `${d.getDate()}. ${d.getMonth() + 1}. ${d.getFullYear()}`;
  }

  valueControl = new FormControl<string>('', { nonNullable: true });
  boolControl = new FormControl<boolean>(false, { nonNullable: true });

  ngOnInit(): void {
    if (this.fieldType === FieldTypeEnum.Boolean) {
      this.boolControl.setValue(this.currentValue === true || this.currentValue === 'true');
      return;
    }

    if (this.fieldType === FieldTypeEnum.Date) {
      this.valueControl.setValue(this.toDateInputValue(this.currentValue));
      return;
    }

    this.valueControl.setValue(this.currentValue === null || this.currentValue === undefined ? '' : `${this.currentValue}`);
  }

  constructor(public activeModal: NgbActiveModal) {}

  submit(): void {
    this.activeModal.close({ newValue: this.resolveNewValue() });
  }

  private resolveNewValue(): unknown {
    if (this.fieldType === FieldTypeEnum.Boolean) {
      return this.boolControl.value;
    }

    const raw = this.valueControl.value?.trim() ?? '';
    if (raw === '') {
      return null;
    }

    if (this.fieldType === FieldTypeEnum.Number) {
      const parsed = Number(raw);
      return Number.isNaN(parsed) ? raw : parsed;
    }

    return raw;
  }

  private toDateInputValue(value: unknown): string {
    if (!value) {
      return '';
    }

    const date = new Date(value as string);
    if (Number.isNaN(date.getTime())) {
      return `${value}`.slice(0, 10);
    }

    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
  }
}
